// SF1 — whether ads make money, and which way it is moving.
//
// Four headline tiles with their ad-type split beneath, six smaller ones, and
// the trend. A rate and its volume belong together: a click rate seen without
// its impressions is misread, and the misreading costs real work.
import React from 'react';
import s from '../AdsAnalytics.module.css';
import { money, num, ratio, IN_POINTS, LOWER_IS_BETTER } from '../format';

const Change = ({ field, value }) => {
  if (value == null) return <span className={s.flat}>—</span>;
  const good = LOWER_IS_BETTER.includes(field) ? value < 0 : value > 0;
  const text = IN_POINTS.includes(field)
    ? `${Math.abs(value * 100).toFixed(1)} pts`
    : `${Math.abs(value * 100).toFixed(0)}%`;
  if (value === 0) return <span className={s.flat}>no change</span>;
  return (
    <span className={good ? s.up : s.down}>{value > 0 ? '▲' : '▼'} {text}</span>
  );
};

export default function Performance({ data, loading, grain, onGrain }) {
  if (loading && !data) return <div className={s.empty}>Loading…</div>;
  if (!data) return null;
  const t = data.totals;
  const c = data.change || {};
  const currency = data.currency;

  if (!t.spend && !t.impressions) {
    return (
      <div className={s.empty}>
        <p>No ads ran in this period.</p>
        <p className={s.hint}>
          The tiles read zero and the ratios are blank — there is nothing to divide.
        </p>
      </div>
    );
  }

  const headline = [
    ['Spend', money(t.spend, currency), 'spend'],
    ['Ad sales', money(t.ad_sales, currency), 'ad_sales'],
    ['ACOS', ratio(t.acos, t.acos_reason), 'acos'],
    ['ROAS', t.roas == null ? t.roas_reason : `${t.roas}×`, 'roas'],
  ];
  const smaller = [
    ['Orders', num(t.orders), 'orders'],
    ['Impressions', num(t.impressions), 'impressions'],
    ['Clicks', num(t.clicks), 'clicks'],
    ['CTR', ratio(t.ctr, t.ctr_reason, 2), 'ctr'],
    ['CPC', t.cpc == null ? t.cpc_reason : money(t.cpc, currency), 'cpc'],
    ['CVR', ratio(t.cvr, t.cvr_reason, 1), 'cvr'],
  ];
  const peak = data.series?.reduce((m, p) => Math.max(m, p.spend, p.ad_sales), 0) || 1;

  return (
    <>
      <div className={s.tileRow}>
        {headline.map(([label, value, field]) => (
          <div key={field} className={s.tile}>
            <span className={s.tileLabel}>{label}</span>
            <span className={s.tileValue}>{value}</span>
            <Change field={field} value={c[field]} />
            {data.by_type?.length > 0 && (
              <div className={s.tileSplit}>
                {data.by_type.map((x) => (
                  <span key={x.ad_product} className={x.skewed ? s.skewed : undefined}>
                    {x.ad_product.replace('SPONSORED_', '').slice(0, 2)}{' '}
                    {field === 'spend' ? money(x.spend, currency)
                      : field === 'ad_sales' ? money(x.ad_sales, currency)
                      : field === 'acos' ? ratio(x.acos, x.acos_reason)
                      : x.roas == null ? '—' : `${x.roas}×`}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={s.tileRow} style={{ marginTop: 12 }}>
        {smaller.map(([label, value, field]) => (
          <div key={field} className={`${s.tile} ${s.tileSmall}`}>
            <span className={s.tileLabel}>{label}</span>
            <span className={s.tileValueSmall}>{value}</span>
            <Change field={field} value={c[field]} />
          </div>
        ))}
      </div>

      <div className={s.trendVerdict}>
        <strong>{data.trend?.trend}</strong> · {data.trend?.why}
      </div>

      <div className={s.controls} style={{ margin: '14px 0 8px' }}>
        <label className={s.field}>
          <span className={s.label}>Chart by</span>
          <select className={s.select} value={grain} onChange={(e) => onGrain(e.target.value)}>
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </label>
      </div>

      <div className={s.chart}>
        {(data.series || []).map((p) => (
          <div key={p.date} className={s.chartCol}
               title={`${p.date} · spend ${money(p.spend, currency)} · sales ${money(p.ad_sales, currency)}`}>
            <div className={s.chartBars}>
              <span className={s.barSpend} style={{ height: `${(p.spend / peak) * 100}%` }} />
              <span className={s.barSales} style={{ height: `${(p.ad_sales / peak) * 100}%` }} />
            </div>
            <span className={`${s.chartLabel} ${p.unsettled ? s.unsettledLabel : ''}`}>
              {p.date.slice(5)}{p.jumped ? ' ⚡' : ''}
            </span>
          </div>
        ))}
      </div>
      <div className={s.chartKey}>
        <span><i className={s.barSpend} /> spend</span>
        <span><i className={s.barSales} /> ad sales</span>
        <span className={s.muted}>⚡ a day more than 40% from its own 7-day average</span>
      </div>
      <p className={s.hint} style={{ marginTop: 10 }}>
        The last {data.window.unsettled_days} days are still updating — Amazon revises clicks
        for three days and orders for up to 28, so those figures are real but not final.
        {' '}{data.attribution}
      </p>
    </>
  );
}
